import * as aq from 'arquero';
import * as React from 'react';

import { type CalendarDateRange } from '../components/DateRangePicker';
import {
  TransactionsContext,
  filterByColumn,
  filterByDateRange,
  filterByAmountType,
} from '../providers/TransactionsProvider';

import type { CalendarDate } from '@internationalized/date';
import type { ColumnTable } from 'arquero';

type TransactionData = {
  id: string;
  amount: number;
  merchantId: string | null;
  financialAccountId: string;
  bankConnectionId: string;
  date: CalendarDate;
  isPaired: boolean;
  isMoneyMovement: boolean;
};

export type Filter = {
  key: keyof TransactionData;
  value: string | number | boolean | null;
};

interface IUseFilteredTransactionsOptions {
  filters?: Filter[];
  dateRange?: CalendarDateRange | null;
  amountType?: 'credit' | 'debit';
  /**
   * Include paired transactions: transfers between the household's bank accounts
   * and the bank side of a transfer into or out of an investment account. Defaults
   * to false for spending calculations.
   */
  includePaired?: boolean;
}

/**
 * Hook that filters transactions by one or more key-value pairs and/or date range.
 * Uses helper functions from TransactionsProvider for filtering.
 * Returns all transactions where all filters match (AND logic).
 *
 * @param options - Filter options with optional filters array and date range
 * @returns Filtered transactions table, loading state, and error state
 */
export const useFilteredTransactions = ({
  filters = [],
  dateRange,
  amountType,
  includePaired = false,
}: IUseFilteredTransactionsOptions = {}) => {
  const { transactions, loading, error } = React.useContext(TransactionsContext);

  const filteredTransactions = React.useMemo<ColumnTable | null>(() => {
    if (!transactions || loading || error) {
      return null;
    }

    let result: ColumnTable = transactions;

    // A paired row is money moved between the household's own accounts, so it is not spending.
    if (!includePaired) {
      result = result.filter(aq.escape((d: { isPaired: boolean }) => !d.isPaired));
    }

    // Apply column filters
    if (filters.length > 0) {
      result = filters.reduce((currentTable, filter) => {
        return filterByColumn(currentTable, filter.key as string, filter.value);
      }, result);
    }

    // Apply date range filter if provided
    if (dateRange) {
      result = filterByDateRange(result, dateRange.start, dateRange.end);
    }

    // Apply amount type filter if provided
    if (amountType) {
      result = filterByAmountType(result, amountType);
    }

    return result;
  }, [transactions, filters, dateRange, amountType, includePaired, loading, error]);

  return {
    transactions: filteredTransactions,
    loading,
    error,
  };
};
